import { onBeforeUnmount, onMounted } from 'vue'
import { isNestedScrollPrevented } from '@/constants/scrollable-overlay.const'

const LISTENER_OPTIONS = { capture: true, passive: true } as const
const MOVE_LISTENER_OPTIONS = { capture: true, passive: false } as const
// scale 浮點數比較留一點誤差空間，避免剛好等於 1 時的浮點誤差被誤判為已縮放
const ZOOM_SCALE_EPSILON = 0.01

/** 是否為多指觸控（例如雙指縮放） */
export const isMultiTouch = (event: Pick<TouchEvent, 'touches'>): boolean =>
  event.touches.length > 1

/** 頁面目前是否處於（瀏覽器原生）縮放放大狀態 */
export const isPageZoomedIn = (visualViewport: VisualViewport | null | undefined): boolean =>
  (visualViewport?.scale ?? 1) > 1 + ZOOM_SCALE_EPSILON

/**
 * 是否該把這次觸控事件完全交還給瀏覽器原生手勢處理（不讓 Lenis 攔截）：
 * - 多指觸控：縮放手勢本身
 * - 頁面已經放大：原生行為是放大後單指仍可各自平移 X/Y 軸可視範圍，
 *   這個平移動作跟一般單指觸控一樣是 touchmove，需要跟「縮放中」一起放行，
 *   否則畫面會卡在剛放大的位置動不了
 */
export const shouldReleaseTouchToNative = (
  event: Pick<TouchEvent, 'touches'>,
  visualViewport: VisualViewport | null | undefined
): boolean => isMultiTouch(event) || isPageZoomedIn(visualViewport)

/**
 * Lenis（syncTouch: true）的觸控 handler 不分單指/多指、也不管頁面有沒有被放大，只要有
 * delta 就一律呼叫 preventDefault，導致兩個瀏覽器原生手勢被吃掉：雙指縮放本身，以及放大後
 * 用單指平移可視範圍。
 *
 * 在 window 的 capture 階段先攔截這兩種情況並呼叫 stopPropagation，讓事件在到達 Lenis
 * 自己（bubble phase）的 listener 之前就被擋下，Lenis 完全不會收到、也就不會呼叫
 * preventDefault，改交給瀏覽器原生處理。獨立捲動區的單指事件仍須抵達元件，否則虛擬
 * 清單收不到 touchstart/touchmove，滑選單反而會平移整個頁面。
 */
export const usePinchZoomGuard = () => {
  const stopTouchPropagationForNativeGesture = (event: TouchEvent) => {
    const path = event.composedPath()
    if (
      event.touches.length === 1 &&
      path.some((node) => node instanceof Element && isNestedScrollPrevented(node))
    ) {
      // 虛擬清單以 JS 捲動；邊界也不能放行原生捲動。auto/scroll 的非虛擬清單則保留原生行為。
      const dropdown = path.find(
        (node): node is HTMLElement =>
          node instanceof HTMLElement && node.classList.contains('ant-select-dropdown')
      )
      // padding 與捲軸是 holder 的外層／兄弟節點，事件路徑不會經過 holder。
      const holder =
        path.find(
          (node): node is HTMLElement =>
            node instanceof HTMLElement && node.classList.contains('rc-virtual-list-holder')
        ) ?? dropdown?.querySelector<HTMLElement>('.rc-virtual-list-holder')
      if (
        event.type === 'touchmove' &&
        event.cancelable &&
        holder &&
        getComputedStyle(holder).overflowY === 'hidden'
      ) {
        event.preventDefault()
      }
      return
    }
    if (shouldReleaseTouchToNative(event, window.visualViewport)) event.stopPropagation()
  }

  onMounted(() => {
    window.addEventListener('touchstart', stopTouchPropagationForNativeGesture, LISTENER_OPTIONS)
    window.addEventListener(
      'touchmove',
      stopTouchPropagationForNativeGesture,
      MOVE_LISTENER_OPTIONS
    )
  })

  onBeforeUnmount(() => {
    window.removeEventListener('touchstart', stopTouchPropagationForNativeGesture, LISTENER_OPTIONS)
    window.removeEventListener(
      'touchmove',
      stopTouchPropagationForNativeGesture,
      MOVE_LISTENER_OPTIONS
    )
  })
}
